<template>
  <SrpModal v-model:isVisible="isVisible" size="medium">
    <template #header>
      <h2 class="global-h2 post-event-history-modal__title">Post Event History</h2>
    </template>
    <template #content>
      <div class="post-event-history-modal">
        <LoadingIcon v-if="isLoading" :size="24" />
        <p v-else-if="!events.length" class="post-event-history-modal__empty">No events yet</p>
        <ul v-else class="post-event-history-modal__list">
          <li v-for="evt in events" :key="evt.id" class="post-event-history-modal__item">
            <div class="post-event-history-modal__item-header">
              <span class="post-event-history-modal__event-type">{{ evt.eventType }}</span>
              <span class="post-event-history-modal__event-date">{{ formatDate(evt.eventDate) }}</span>
              <span v-if="evt.userId" class="post-event-history-modal__user-id">{{ evt.userId }}</span>
              <LinkWithIcon
                v-if="evt.blobPath"
                isDottedUnderline
                color="blue"
                @click="toggleDiff(evt.id)"
              >
                <template #icon><IconEmbedded name="chevron-down_2-5" :size="14" /></template>
                {{ expandedDiffId === evt.id ? "Hide diff" : "View diff" }}
              </LinkWithIcon>
            </div>
            <p v-if="evt.summary" class="post-event-history-modal__summary">{{ evt.summary }}</p>
            <div v-if="expandedDiffId === evt.id" class="post-event-history-modal__diff-wrap">
              <LoadingIcon v-if="diffLoadingId === evt.id" :size="18" />
              <pre v-else-if="diffByEventId[evt.id]" class="post-event-history-modal__diff">{{ diffByEventId[evt.id] }}</pre>
              <p v-else-if="diffErrorByEventId[evt.id]" class="post-event-history-modal__diff-error">{{ diffErrorByEventId[evt.id] }}</p>
            </div>
          </li>
        </ul>
      </div>
    </template>
    <template #footer>
      <SrpButton color="gray" fill="outlined" @click="isVisible = false">Close</SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { shrpaApi } from "@repos/axios";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

type SocialPostEventSummary = {
  id: string;
  eventType: string;
  eventDate: string;
  userId: string;
  summary: string;
  blobPath: string | null;
};

const props = defineProps<{
  customerId: string;
  postId: string;
}>();

const isVisible = ref(false);
const isLoading = ref(false);
const events = ref<SocialPostEventSummary[]>([]);
const expandedDiffId = ref<string | null>(null);
const diffLoadingId = ref<string | null>(null);
const diffByEventId = ref<Record<string, string>>({});
const diffErrorByEventId = ref<Record<string, string>>({});

async function fetchEvents() {
  if (!props.customerId || !props.postId) return;
  isLoading.value = true;
  events.value = [];
  try {
    const { data } = await shrpaApi.get<SocialPostEventSummary[]>(
      `/social/v2/${encodeURIComponent(props.customerId)}/posts/${encodeURIComponent(props.postId)}/events`
    );
    events.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("PostEventHistoryModal: failed to fetch events", err);
    events.value = [];
  } finally {
    isLoading.value = false;
  }
}

async function fetchDiff(eventId: string) {
  if (diffByEventId.value[eventId] || diffErrorByEventId.value[eventId]) return;
  diffLoadingId.value = eventId;
  diffErrorByEventId.value = { ...diffErrorByEventId.value, [eventId]: "" };
  try {
    const { data } = await shrpaApi.get<unknown>(
      `/social/v2/${encodeURIComponent(props.customerId)}/posts/${encodeURIComponent(props.postId)}/events/${encodeURIComponent(eventId)}/diff`
    );
    diffByEventId.value = { ...diffByEventId.value, [eventId]: JSON.stringify(data, null, 2) };
  } catch (err) {
    const msg = err && typeof err === "object" && "message" in err ? String((err as Error).message) : "Failed to load diff";
    diffErrorByEventId.value = { ...diffErrorByEventId.value, [eventId]: msg };
  } finally {
    diffLoadingId.value = null;
  }
}

function toggleDiff(eventId: string) {
  if (expandedDiffId.value === eventId) {
    expandedDiffId.value = null;
    return;
  }
  expandedDiffId.value = eventId;
  fetchDiff(eventId);
}

function formatDate(s: string) {
  if (!s) return "";
  try {
    const d = new Date(s);
    return d.toLocaleString();
  } catch {
    return s;
  }
}

watch(isVisible, visible => {
  if (visible) {
    expandedDiffId.value = null;
    diffByEventId.value = {};
    diffErrorByEventId.value = {};
    fetchEvents();
  }
});

watch(
  () => [props.customerId, props.postId] as const,
  () => {
    if (isVisible.value) fetchEvents();
  }
);

defineExpose({
  openModal: () => {
    isVisible.value = true;
  },
});
</script>

<style scoped lang="scss">
.post-event-history-modal {
  min-height: 80px;

  &__title {
    margin: 0;
  }

  &__empty {
    color: #666;
    margin: 0;
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__item {
    border-bottom: 1px solid #eee;
    padding: 10px 0;

    &:last-child {
      border-bottom: none;
    }
  }

  &__item-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  &__event-type {
    font-weight: 600;
  }

  &__event-date {
    color: #666;
    font-size: 0.9em;
  }

  &__user-id {
    color: #888;
    font-size: 0.85em;
  }

  &__summary {
    margin: 4px 0 0;
    font-size: 0.9em;
    color: #444;
  }

  &__diff-wrap {
    margin-top: 10px;
    padding: 10px;
    background: #f8f8f8;
    border-radius: 4px;
  }

  &__diff {
    margin: 0;
    font-size: 12px;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 300px;
    overflow: auto;
  }

  &__diff-error {
    margin: 0;
    color: #c00;
    font-size: 0.9em;
  }
}
</style>
